import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSendTransaction, useWallets } from '@privy-io/react-auth';
import { useWalletClient } from 'wagmi';
import { AcrossClient } from '@across-protocol/app-sdk';
import { encodeFunctionData } from 'viem';
import { ACROSS_CHAINS } from '../config/across';
import { ERC20_ABI } from '../config/oft';
import { ensureAcrossDepositAllowance } from '../utils/acrossAllowance';
import { getAcrossProgressUpdate } from '../utils/acrossProgress';
import { getSourceChainPublicClient } from '../utils/getSourceChainPublicClient';

// Singleton Across client — initialized once
let acrossClient: any;

function getAcrossClient() {
  if (!acrossClient) {
    acrossClient = AcrossClient.create({
      chains: [...ACROSS_CHAINS],
    });
  }
  return acrossClient;
}

export interface AcrossQuoteData {
  /** Estimated output amount in output token base units */
  outputAmount: bigint;
  /** Total relay fee in input token base units */
  relayFeeTotal: bigint;
  /** Estimated fill time in seconds */
  estimatedFillTimeSec: number;
  /** The full quote object for executeQuote */
  quote: unknown;
}

export interface AcrossQuoteParams {
  sourceChainId: number | undefined;
  destinationChainId: number | undefined;
  inputToken: `0x${string}` | undefined;
  outputToken: `0x${string}` | undefined;
  amount: bigint | undefined;
  recipient: `0x${string}` | undefined;
}

export interface AcrossExecuteResult {
  sourceTxHash: `0x${string}` | undefined;
  fillTxHash: `0x${string}` | undefined;
  fillConfirmed: boolean;
}

interface AcrossQuoteDeposit {
  inputAmount: bigint | string;
  inputToken: `0x${string}`;
  isNative?: boolean;
  originChainId: number;
  spokePoolAddress: `0x${string}`;
}

type SponsoredTransactionSender = (params: {
  to: `0x${string}`;
  data: `0x${string}`;
  value?: bigint;
  chainId: number;
  description: string;
  buttonText: string;
}) => Promise<`0x${string}` | undefined>;

type AcrossProgressCallback = (
  step: string,
  status: string,
  txHash?: `0x${string}`,
) => void;

/**
 * Gets a quote from Across Protocol for the exact route/token tuple.
 */
export function useAcrossQuote(params: AcrossQuoteParams) {
  const { sourceChainId, destinationChainId, inputToken, outputToken, amount, recipient } = params;

  return useQuery<AcrossQuoteData>({
    queryKey: [
      'acrossQuote',
      sourceChainId,
      destinationChainId,
      inputToken,
      outputToken,
      amount?.toString(),
      recipient,
    ],
    queryFn: async () => {
      if (!sourceChainId || !destinationChainId || !inputToken || !outputToken || !amount || !recipient) {
        throw new Error('sourceChainId, destinationChainId, inputToken, outputToken, amount, and recipient are required');
      }

      const client = getAcrossClient();

      const quote = await client.getQuote({
        route: {
          originChainId: sourceChainId,
          destinationChainId,
          inputToken,
          outputToken,
        },
        inputAmount: amount,
        recipient,
      } as any);

      const outputAmount = BigInt(quote.deposit.outputAmount.toString());
      return {
        outputAmount,
        relayFeeTotal: amount > outputAmount ? amount - outputAmount : BigInt("0"),
        estimatedFillTimeSec: quote.estimatedFillTimeSec ?? 2,
        quote,
      };
    },
    enabled: !!sourceChainId && !!destinationChainId && !!inputToken && !!outputToken && !!amount && amount > BigInt("0") && !!recipient,
    staleTime: 30_000,
  });
}

/**
 * Executes an Across bridge to send the quoted token from source chain to Ink.
 * Handles approval + deposit in one flow.
 *
 * @param sourceChainId - EVM chain ID of the source chain
 */
export function useAcrossExecute(sourceChainId: number | undefined) {
  // Don't filter by chainId — Across SDK handles chain switching internally
  const { data: walletClient } = useWalletClient();
  const { sendTransaction } = useSendTransaction();
  const { wallets } = useWallets();
  const queryClient = useQueryClient();

  const sendSponsoredTransaction: SponsoredTransactionSender = async ({
    to,
    data,
    value,
    chainId,
    description,
    buttonText,
  }) => {
    const accountAddress = walletClient?.account?.address;
    if (!accountAddress) return undefined;

    const embeddedWallet = wallets.find(
      (wallet) =>
        wallet.walletClientType === 'privy' &&
        wallet.address.toLowerCase() === accountAddress.toLowerCase(),
    );
    if (!embeddedWallet) return undefined;

    const result = await sendTransaction(
      {
        to,
        data,
        value,
        chainId,
      },
      {
        address: embeddedWallet.address,
        sponsor: true,
        uiOptions: {
          description,
          buttonText,
        },
      },
    );

    return result.hash;
  };

  return useMutation({
    mutationFn: async (params: {
      quote: unknown;
      onProgress?: AcrossProgressCallback;
    }): Promise<AcrossExecuteResult> => {
      if (!walletClient) throw new Error('Wallet not connected');

      const client = getAcrossClient();

      // Source deposit confirmation is bridge_submitted. Delivery is verified
      // separately by fill evidence plus Ink balance polling.
      let depositTxHash: `0x${string}` | undefined;
      let fillTxHash: `0x${string}` | undefined;
      let depositConfirmed = false;
      let fillConfirmed = false;

      try {
        const deposit = (params.quote as any).deposit as AcrossQuoteDeposit;
        const skipAllowanceCheck = deposit
          ? await ensureAcrossDepositAllowance({
              deposit,
              accountAddress: walletClient.account?.address,
              publicClient: getSourceChainPublicClient(deposit.originChainId),
              sendApproveTransaction: (approval) =>
                sendApproveTransaction({
                  walletClient: walletClient as any,
                  sendSponsoredTransaction,
                  ...approval,
                }),
              onProgress: params.onProgress,
            })
          : false;
        const executionWalletClient = createSponsoredAcrossWalletClient({
          walletClient: walletClient as any,
          sendSponsoredTransaction,
        });

        await client.executeQuote({
          walletClient: executionWalletClient,
          deposit,
          skipAllowanceCheck,
          onProgress: (progress: any) => {
            const update = getAcrossProgressUpdate(progress);
            console.log('[Across] progress:', update.step, update.status);
            if (update.step === 'approve' && update.status === 'success') {
              params.onProgress?.('approve', 'success', update.txHash);
            }
            if (update.step === 'deposit' && update.status === 'success') {
              depositTxHash = update.txHash;
              depositConfirmed = true;
              console.log('[Across] Deposit confirmed:', depositTxHash);
              params.onProgress?.('deposit', 'success', depositTxHash);
            }
            if (update.step === 'fill' && update.status === 'success') {
              fillTxHash = update.txHash;
              fillConfirmed = true;
              console.log('[Across] Fill confirmed on Ink:', fillTxHash);
              params.onProgress?.('fill', 'success', fillTxHash);
            }
          },
        });
      } catch (err) {
        // If the source deposit was confirmed but the fill check failed, return
        // bridge_submitted and let Ink balance polling decide delivery.
        if (depositConfirmed) {
          console.log('[Across] executeQuote threw after source deposit confirmed');
          return { sourceTxHash: depositTxHash, fillTxHash, fillConfirmed };
        }
        throw err; // Re-throw if deposit didn't confirm
      }

      return { sourceTxHash: depositTxHash, fillTxHash, fillConfirmed };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['sourceTokenBalance', sourceChainId],
      });
    },
  });
}

async function sendApproveTransaction({
  walletClient,
  sendSponsoredTransaction,
  tokenAddress,
  spender,
  amount,
  chainId,
  description,
}: {
  walletClient: any;
  sendSponsoredTransaction: SponsoredTransactionSender;
  tokenAddress: `0x${string}`;
  spender: `0x${string}`;
  amount: bigint;
  chainId: number;
  description: string;
}): Promise<`0x${string}`> {
  const data = encodeFunctionData({
    abi: ERC20_ABI,
    functionName: 'approve',
    args: [spender, amount],
  });

  const sponsoredHash = await sendSponsoredTransaction({
    to: tokenAddress,
    data,
    chainId,
    description,
    buttonText: 'Approve',
  });
  if (sponsoredHash) {
    return sponsoredHash;
  }

  return walletClient.writeContract({
    address: tokenAddress,
    abi: ERC20_ABI,
    functionName: 'approve',
    args: [spender, amount],
    chain: walletClient.chain,
    account: walletClient.account,
  });
}

function createSponsoredAcrossWalletClient({
  walletClient,
  sendSponsoredTransaction,
}: {
  walletClient: any;
  sendSponsoredTransaction: SponsoredTransactionSender;
}) {
  return {
    ...walletClient,
    writeContract: async (request: any): Promise<`0x${string}`> => {
      if (request.address && request.abi && request.functionName) {
        const chainId =
          request.chain?.id ?? walletClient.chain?.id ?? await walletClient.getChainId();
        const data = appendHexDataSuffix(
          encodeFunctionData({
            abi: request.abi,
            functionName: request.functionName,
            args: request.args ?? [],
          } as any),
          request.dataSuffix,
        );
        const sponsoredHash = await sendSponsoredTransaction({
          to: request.address,
          data,
          value: request.value,
          chainId,
          description:
            request.functionName === 'deposit'
              ? 'Bridge funds to Ink with Across'
              : `Confirm ${String(request.functionName)}`,
          buttonText: request.functionName === 'deposit' ? 'Bridge' : 'Confirm',
        });
        if (sponsoredHash) {
          return sponsoredHash;
        }
      }

      return walletClient.writeContract(request);
    },
  };
}

function appendHexDataSuffix(
  data: `0x${string}`,
  dataSuffix: `0x${string}` | undefined,
): `0x${string}` {
  if (!dataSuffix || dataSuffix === '0x') {
    return data;
  }

  return `${data}${dataSuffix.slice(2)}` as `0x${string}`;
}
